import type { PointerEvent as ReactPointerEvent } from 'react';
import type { ConnectionId } from '@/types';
import { useSelectionStore } from '@/store/selectionStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useCanvasInteractionController } from '@/features/canvas/CanvasInteractionContext';
import { useEngine } from '@/app/EngineContext';
import { connectionSegment } from '@/core/graph/connectionGeometry';
import styles from './SelectionHandles.module.css';

/** Draggable end grips for a single selected connection (reconnect gesture). */
export const ConnectionHandles = () => {
  const ids = useSelectionStore((s) => s.selection.connectionIds);
  const only = ids.length === 1 ? ids[0] : undefined;
  return only ? <HandlesFor id={only} /> : null;
};

const HandlesFor = ({ id }: { id: ConnectionId }) => {
  const { beginReconnect } = useCanvasInteractionController();
  const { scene, ensureSynced } = useEngine();
  const key = useWorkspaceStore((s) => {
    const c = s.workspaces[s.activeWorkspaceId]?.connections[id];
    if (!c) return null;
    ensureSynced();
    const sb = scene.boundsOf(scene.visibleAnchorOf(c.sourceId));
    const tb = scene.boundsOf(scene.visibleAnchorOf(c.targetId));
    if (!sb || !tb) return null;
    const seg = connectionSegment(sb, tb);
    return `${seg.a.x},${seg.a.y},${seg.b.x},${seg.b.y}`;
  });
  if (!key) return null;
  const [ax = 0, ay = 0, bx = 0, by = 0] = key.split(',').map(Number);
  const grip = (end: 'source' | 'target', x: number, y: number) => (
    <span
      className={styles.rotate}
      style={{ left: x, top: y, marginTop: -7, position: 'absolute' }}
      data-testid={`connection-grip-${end}`}
      onPointerDown={(e: ReactPointerEvent<HTMLElement>) => {
        if (e.button === 0) beginReconnect(e, id, end);
      }}
    />
  );
  return (
    <div className={styles.connectionFrame} aria-hidden="true">
      {grip('source', ax, ay)}
      {grip('target', bx, by)}
    </div>
  );
};
